import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';
import { SelectPesquisavelComponent } from '../../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { PerfilOptionStruct } from '../../../../../../shared/services/structs/usuario/perfil-option.struct';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { ConfigKanbanService } from '../../../../../../shared/services/apis/vendas/config-kanban.service';
import { MotivoTransicaoService } from '../../../../../../shared/services/apis/vendas/motivo-transicao.service';
import { EtapaKanbanSalvarRequest } from '../../../../../../shared/services/requests/vendas/etapa-kanban-salvar.request';
import { KanbanProcessoStruct } from '../../../../../../shared/services/structs/vendas/kanban-processo.struct';
import { EtapaKanbanStruct } from '../../../../../../shared/services/structs/vendas/etapa-kanban.struct';
import { KanbanCor } from '../../../../../../shared/services/models/vendas/kanban-cor.model';
import { KanbanIcone } from '../../../../../../shared/services/models/vendas/kanban-icone.model';
import { MotivoTransicao } from '../../../../../../shared/services/models/vendas/motivo-transicao.model';
import { TipoEtapaKanbanEnum } from '../../../../../../shared/enums/vendas/tipo-etapa-kanban.enum';
import {
  KanbanMarcadorTransferenciaDialogComponent,
} from '../kanban-marcador-transferencia-dialog/kanban-marcador-transferencia-dialog.component';

export interface EtapaKanbanModalData {
  etapa: EtapaKanbanStruct | null;
  idTipoProcessoKanban: number;
  tipoNome: string;
  tipoIcones: Record<number, string>;
  processos: KanbanProcessoStruct[];
  cores: KanbanCor[];
  icones: KanbanIcone[];
  perfis: PerfilOptionStruct[];
}

/** Um destino candidato na grade "Movimentações permitidas". */
export interface KanbanDestinoOption {
  etapa: EtapaKanbanStruct;
  // Especial recebe o selo próprio: encerra o card fora do fluxo linear.
  isEspecial: boolean;
}

export interface KanbanGrupoDestino {
  processo: KanbanProcessoStruct;
  isOutroProcesso: boolean;
  destinos: KanbanDestinoOption[];
}

export type EtapaKanbanModalResult = { action: 'saved' } | { action: 'delete'; idEtapaKanban: number } | undefined;

@Component({
  selector: 'app-etapa-kanban-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatRadioModule,
    MatTooltipModule,
    AlertCardComponent,
    SelectPesquisavelComponent,
  ],
  templateUrl: './etapa-kanban-modal.component.html',
  styleUrls: ['./etapa-kanban-modal.component.css'],
})
export class EtapaKanbanModalComponent implements OnInit {
  public isEdicao = false;
  public isSaving = false;

  public nome = '';
  public idKanbanCor = 1;
  public idKanbanIcone = 1;
  public isInicial = false;
  public isFinal = false;

  public visibilidadeTodos = true;
  public visibilidadeControl = new FormControl<number[]>([], { nonNullable: true });

  public isMotivoObrigatorio = false;
  public motivosControl = new FormControl<number[]>([], { nonNullable: true });
  public motivosAtivos: MotivoTransicao[] = [];

  // destino (idEtapaKanban) -> FormControl de perfis. Presença na Map = movimentação permitida.
  public movControls = new Map<number, FormControl<number[]>>();

  constructor(
    public dialogRef: MatDialogRef<EtapaKanbanModalComponent, EtapaKanbanModalResult>,
    @Inject(MAT_DIALOG_DATA) public data: EtapaKanbanModalData,
    private dialog: MatDialog,
    private configKanbanService: ConfigKanbanService,
    private motivoTransicaoService: MotivoTransicaoService,
    private alertService: AlertService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const s = this.data.etapa;
    this.isEdicao = !!s;
    if (s) {
      this.nome = s.nome;
      this.idKanbanCor = s.idKanbanCor;
      this.idKanbanIcone = s.idKanbanIcone;
      this.isInicial = s.isInicial;
      this.isFinal = s.isFinal;
      this.visibilidadeTodos = (s.idsPerfilVisibilidade?.length ?? 0) === 0;
      this.visibilidadeControl.setValue([...(s.idsPerfilVisibilidade ?? [])]);
      this.isMotivoObrigatorio = s.isMotivoObrigatorio;
      this.motivosControl.setValue([...(s.idsMotivoTransicao ?? [])]);
      for (const mov of s.movimentacoes ?? []) {
        this.movControls.set(mov.idEtapaKanbanDestino, new FormControl<number[]>([...mov.idsPerfil], { nonNullable: true }));
      }
    }

    if (!this.isFixo) {
      this.carregarMotivos();
    }
  }

  // ---- Natureza da etapa ----

  /** Etapa fixa do sistema: só Identidade e Visibilidade são editáveis. */
  public get isFixo(): boolean {
    return this.data.etapa?.isFixo ?? false;
  }

  public get idTipoEtapaKanban(): number {
    return this.data.etapa?.idTipoEtapaKanban ?? TipoEtapaKanbanEnum.FluxoPrincipal;
  }

  /**
   * Só o fluxo principal tem papel no fluxo, grade de movimentações e motivo obrigatório.
   * Vale inclusive para a etapa FIXA do fluxo (Em aberto/Aprovado): nela o que trava é o nome e
   * a posição — as cinco seções continuam disponíveis, diferente das etapas especiais.
   */
  public get mostraSecoesDeFluxo(): boolean {
    return this.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal;
  }

  /** Nota explicativa exibida nas etapas fixas. */
  public get notaEtapaFixa(): string {
    if (this.idTipoEtapaKanban === TipoEtapaKanbanEnum.EnvioRetirada) {
      return `Marcador fixo do sistema, ativado em Configurações >> Vendas >> Geral. Fica fora do fluxo linear: `
        + `o card entra e sai desta faixa por eventos da rotina externa de ${this.data.etapa?.nome} — envio, `
        + `atualização de progresso e conclusão, não por arraste. Você pode ajustar cor, ícone e visibilidade.`;
    }
    if (this.idTipoEtapaKanban === TipoEtapaKanbanEnum.Especial) {
      return 'Etapa especial fixa do sistema: fica fora do fluxo linear, não participa de Inicial → Final, '
        + 'não é reordenável nem excluível. Você pode ajustar cor, ícone e visibilidade.';
    }
    // Fixo dentro do fluxo principal (Em aberto / Aprovado do Pedido de Venda).
    return 'Etapa exigida pelas automações do sistema: o nome e a posição no fluxo são fixos e a etapa não pode '
      + 'ser excluída, porque automações se apoiam na transição para ela. Você pode ajustar cor, ícone, '
      + 'visibilidade, destinos permitidos e perfis.';
  }

  // ---- Numeração das seções (etapa fora do fluxo principal tem só duas) ----

  public get numeroSecaoVisibilidade(): number {
    return this.mostraSecoesDeFluxo ? 3 : 2;
  }

  // ---- Motivos de transição ----

  private carregarMotivos(): void {
    // Só motivos ativos são oferecidos; tamanho alto para trazer o catálogo inteiro de uma vez.
    this.motivoTransicaoService.getAll('', true, 0, 500).subscribe({
      next: res => {
        if (res.isError) return;
        this.motivosAtivos = res.listaMotivos || [];
      },
      error: () => { /* silencioso: a seção apenas fica sem opções */ },
    });
  }

  public get motivosOptions(): { idPerfil: number; nome: string }[] {
    // Reaproveita o contrato do select-pesquisavel, que espera { idPerfil, nome }.
    return this.motivosAtivos.map(m => ({ idPerfil: m.idMotivoTransicao, nome: m.nome }));
  }

  public get resumoMotivos(): string {
    const qtd = this.motivosControl.value?.length ?? 0;
    if (qtd === 0) {
      return this.motivosAtivos.length
        ? 'Todos os motivos ativos ficam disponíveis nesta etapa.'
        : 'Nenhum motivo ativo cadastrado — cadastre antes de usar.';
    }
    return `${qtd} motivo(s) selecionado(s).`;
  }

  public gerenciarMotivos(): void {
    // Navega para o cadastro (nunca modal sobre modal).
    this.dialogRef.close(undefined);
    this.router.navigate(['/configuracoes/vendas/motivos-transicao']);
  }

  // ---- Grade de movimentações ----

  /**
   * Destinos oferecidos: etapas do fluxo principal do próprio processo e do processo seguinte
   * (a conversão Orçamento → Pedido de Venda, de mão única), mais as etapas especiais do próprio
   * processo. Ficam de fora, sempre: o Modelo e os marcadores de "Envio e retirada" — eles não
   * são alcançados por movimentação configurada.
   */
  public get gruposDestino(): KanbanGrupoDestino[] {
    const selfId = this.data.etapa?.idEtapaKanban ?? 0;

    return this.data.processos
      .filter(p => p.isAtivo)
      .map(p => {
        const isOutroProcesso = p.idTipoProcessoKanban !== this.data.idTipoProcessoKanban;
        const destinos: KanbanDestinoOption[] = p.etapas
          .filter(s => s.idEtapaKanban !== selfId)
          .filter(s => s.chave !== 'Modelo')
          .filter(s => s.idTipoEtapaKanban !== TipoEtapaKanbanEnum.EnvioRetirada)
          // Especial só é oferecido dentro do próprio processo.
          .filter(s => !isOutroProcesso || s.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal)
          .map(s => ({ etapa: s, isEspecial: s.idTipoEtapaKanban === TipoEtapaKanbanEnum.Especial }));

        return { processo: p, isOutroProcesso, destinos };
      })
      // A conversão é de mão única: ao editar o Pedido de Venda, o Orçamento não é destino.
      .filter(g => !g.isOutroProcesso || this.permiteConversaoPara(g.processo))
      .filter(g => g.destinos.length > 0);
  }

  /** Hoje o único par cross-processo é Orçamento → Pedido de Venda. */
  private permiteConversaoPara(processo: KanbanProcessoStruct): boolean {
    return this.data.idTipoProcessoKanban < processo.idTipoProcessoKanban;
  }

  public get notaGrade(): string {
    const temConversao = this.gruposDestino.some(g => g.isOutroProcesso);
    return temConversao
      ? 'A partir do Orçamento, o único destino em outro processo é Pedido de venda. Marcar um destino define '
        + 'apenas a permissão de movimentação manual do card — não gera documentos automaticamente.'
      : 'A conversão é de mão única: o processo Orçamento não é oferecido como destino a partir do Pedido de '
        + 'venda. Marcar um destino define apenas a permissão de movimentação manual do card.';
  }

  public get qtdMovimentacoes(): number {
    return this.movControls.size;
  }

  public isDestino(idEtapaKanban: number): boolean {
    return this.movControls.has(idEtapaKanban);
  }

  public toggleDestino(idEtapaKanban: number, checked: boolean): void {
    if (checked) {
      if (!this.movControls.has(idEtapaKanban)) {
        this.movControls.set(idEtapaKanban, new FormControl<number[]>([], { nonNullable: true }));
      }
    } else {
      this.movControls.delete(idEtapaKanban);
    }
  }

  public perfilControl(idEtapaKanban: number): FormControl<number[]> {
    return this.movControls.get(idEtapaKanban)!;
  }

  // ---- Identidade ----

  public selecionarCor(id: number): void {
    this.idKanbanCor = id;
  }

  public selecionarIcone(id: number): void {
    this.idKanbanIcone = id;
  }

  public iconeNome(idKanbanIcone: number): string {
    return this.data.icones.find(i => i.idKanbanIcone === idKanbanIcone)?.nomeIcone ?? 'label';
  }

  public iconeNomeSelecionado(): string {
    return this.iconeNome(this.idKanbanIcone);
  }

  // Sem fallback: id_kanban_cor é FK NOT NULL, então sempre resolve. Se o lookup não tiver
  // carregado, o binding fica indefinido e o CSS aplica o valor default do próprio var().
  public corPorId(idKanbanCor: number): KanbanCor | undefined {
    return this.data.cores.find(c => c.idKanbanCor === idKanbanCor);
  }

  public corSelecionada(): KanbanCor | undefined {
    return this.corPorId(this.idKanbanCor);
  }

  public onInicialChange(checked: boolean): void {
    this.isInicial = checked;
    if (!checked) return;

    // Já existe outro inicial neste processo? → confirma transferência.
    const processo = this.data.processos.find(p => p.idTipoProcessoKanban === this.data.idTipoProcessoKanban);
    const selfId = this.data.etapa?.idEtapaKanban ?? 0;
    const inicialAtual = processo?.etapas.find(s => s.isInicial && s.idEtapaKanban !== selfId);
    if (!inicialAtual) return;

    const ref = this.dialog.open(KanbanMarcadorTransferenciaDialogComponent, {
      width: '460px',
      data: { tipoNome: this.data.tipoNome, nomeAnterior: inicialAtual.nome, nomeNovo: this.nome || 'esta etapa' },
    });
    ref.afterClosed().subscribe(confirmou => {
      if (!confirmou) {
        this.isInicial = false;
      }
    });
  }

  public salvar(): void {
    const nome = this.nome.trim();
    if (!nome) {
      this.alertService.show('Atenção', 'Informe o nome da etapa.', AlertType.warning);
      return;
    }

    // O nome de etapa fixa não é editável, então também não entra na checagem de duplicidade.
    if (!this.isFixo) {
      const selfId = this.data.etapa?.idEtapaKanban ?? 0;
      const processo = this.data.processos.find(p => p.idTipoProcessoKanban === this.data.idTipoProcessoKanban);
      const nomeDuplicado = processo?.etapas.some(
        s => s.idEtapaKanban !== selfId && s.nome.trim().toLowerCase() === nome.toLowerCase()
      );
      if (nomeDuplicado) {
        this.alertService.show('Atenção', 'Já existe uma etapa com este nome neste processo.', AlertType.warning);
        return;
      }
    }

    const request: EtapaKanbanSalvarRequest = {
      idEtapaKanban: this.data.etapa?.idEtapaKanban ?? null,
      idTipoProcessoKanban: this.data.idTipoProcessoKanban,
      nome,
      idKanbanCor: this.idKanbanCor,
      idKanbanIcone: this.idKanbanIcone,
      isInicial: this.isInicial,
      isFinal: this.isFinal,
      idsPerfilVisibilidade: this.visibilidadeTodos ? [] : (this.visibilidadeControl.value ?? []),
      // Etapa fora do fluxo principal não tem saída própria — o backend rejeita se vier preenchido.
      movimentacoes: this.mostraSecoesDeFluxo
        ? Array.from(this.movControls.entries()).map(([idDestino, ctrl]) => ({
            idEtapaKanbanDestino: idDestino,
            idsPerfil: ctrl.value ?? [],
          }))
        : [],
      isMotivoObrigatorio: this.mostraSecoesDeFluxo && this.isMotivoObrigatorio,
      idsMotivoTransicao: this.mostraSecoesDeFluxo && this.isMotivoObrigatorio
        ? (this.motivosControl.value ?? [])
        : [],
    };

    this.isSaving = true;
    this.configKanbanService.salvarEtapa(request).subscribe({
      next: res => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Etapa salva com sucesso.', AlertType.success);
        this.dialogRef.close({ action: 'saved' });
      },
      error: err => {
        this.isSaving = false;
        this.alertService.show('Erro', err.message || 'Erro ao salvar a etapa.', AlertType.error);
      },
    });
  }

  public excluir(): void {
    if (!this.data.etapa) return;
    this.dialogRef.close({ action: 'delete', idEtapaKanban: this.data.etapa.idEtapaKanban });
  }

  public cancelar(): void {
    this.dialogRef.close(undefined);
  }
}
